/**
 * Steuer-Panel der Liefertermin-Kundeninfo — von beiden Tools (glawel/sporttore)
 * genutzt. Bedient dieselbe geteilte Settings-Zeile ueber den jeweiligen
 * Programm-Namespace (/programmes/<id>/kundeninfo).
 *
 * Schalter: Global-Versand an/aus (Kill-Switch), Test/Live, Test-Auftragsnummern,
 * Test-Mail-Button. CI-Farben: #E9FF57 / #1A1A1A / #DAD9D9.
 */

import { useState, useEffect } from 'react';
import { api } from '@libero/core/services/api';
import { Mail, ShieldAlert, ShieldCheck, Send, Save } from 'lucide-react';

interface KundeninfoSettings {
  emailVersandAktiv: boolean;
  testModus: boolean;
  testAuftragsNummern: string;
  testRecipient: string;
  templates: { erstinfo: number; update: number };
  maxMailsProLauf: number;
}

export default function KundeninfoPanel({ programmeId }: { programmeId: string }) {
  const base = `/programmes/${programmeId}/kundeninfo`;

  const [settings, setSettings] = useState<KundeninfoSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testOrderId, setTestOrderId] = useState('');
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [diagnose, setDiagnose] = useState<string | null>(null);
  const [cdKw, setCdKw] = useState('KW34');

  useEffect(() => {
    (async () => {
      try {
        const resp = await (api as any).get(base);
        if (resp.data?.success) setSettings(resp.data.data);
      } catch (e: any) {
        setMsg({ text: 'Einstellungen konnten nicht geladen werden.', ok: false });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function patch(p: Partial<KundeninfoSettings>) {
    setSettings((s) => (s ? { ...s, ...p } : s));
  }

  async function save() {
    if (!settings) return;
    setSaving(true);
    setMsg(null);
    try {
      const resp = await (api as any).put(base, {
        emailVersandAktiv: settings.emailVersandAktiv,
        testModus: settings.testModus,
        testAuftragsNummern: settings.testAuftragsNummern,
      });
      if (resp.data?.success) {
        patch(resp.data.data);
        setMsg({ text: 'Gespeichert.', ok: true });
      }
    } catch (e: any) {
      setMsg({ text: 'Speichern fehlgeschlagen: ' + (e.response?.data?.error?.message || e.message), ok: false });
    } finally {
      setSaving(false);
    }
  }

  async function sendTestMail() {
    const orderId = testOrderId.trim();
    if (!orderId) {
      setMsg({ text: 'Bitte eine Auftragsnummer für die Test-Mail eintragen.', ok: false });
      return;
    }
    setMsg(null);
    try {
      const resp = await (api as any).post(`${base}/test-mail`, { orderId });
      if (resp.data?.success) {
        const pr = resp.data.data?.plentyResponse;
        const prText = pr ? ' · Plenty-Antwort: ' + JSON.stringify(pr).slice(0, 200) : '';
        setMsg({ text: `Test-Mail für Auftrag ${orderId} an ${resp.data.data.recipient} ausgelöst.${prText}`, ok: true });
      }
    } catch (e: any) {
      setMsg({ text: 'Test-Mail fehlgeschlagen: ' + (e.response?.data?.error?.message || e.message), ok: false });
    }
  }

  async function runDiagnose() {
    setMsg(null);
    setDiagnose('Lade Diagnose…');
    try {
      const q = testOrderId.trim() ? `?orderId=${encodeURIComponent(testOrderId.trim())}` : '';
      const resp = await (api as any).get(`${base}/diagnose-templates${q}`);
      setDiagnose(JSON.stringify(resp.data?.data ?? resp.data, null, 2));
    } catch (e: any) {
      setDiagnose('Diagnose fehlgeschlagen: ' + (e.response?.data?.error?.message || e.message));
    }
  }

  async function runChangeDetection() {
    const orderId = testOrderId.trim();
    if (!orderId || !cdKw.trim()) {
      setMsg({ text: 'Auftragsnummer (oben) und KW eintragen.', ok: false });
      return;
    }
    setMsg(null);
    try {
      const resp = await (api as any).post(`${base}/test-changedetection`, { orderId, kw: cdKw.trim() });
      const r = resp.data?.data;
      setMsg({
        text: `Änderungserkennung: ${r?.outcome} — ${r?.reason}` + (r?.sent ? ' (Mail gesendet)' : ''),
        ok: !!r && r.outcome !== 'fehler',
      });
    } catch (e: any) {
      setMsg({ text: 'Test fehlgeschlagen: ' + (e.response?.data?.error?.message || e.message), ok: false });
    }
  }

  const cardStyle: React.CSSProperties = {
    background: '#fff', border: '1px solid #DAD9D9', borderRadius: '8px', marginBottom: '1rem',
  };
  const labelStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' };

  const versandAn = !!settings?.emailVersandAktiv;
  const live = settings ? !settings.testModus : false;

  return (
    <div style={cardStyle}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        padding: '0.75rem 1rem', borderBottom: '1px solid #DAD9D9',
      }}>
        <Mail size={18} />
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Kundeninfo Liefertermin (E-Mail)</h3>
        {settings && (
          <span style={{
            marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.3rem',
            padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700,
            backgroundColor: versandAn ? (live ? '#c62828' : '#E9FF57') : '#DAD9D9',
            color: versandAn && live ? '#fff' : '#1A1A1A',
          }}>
            {versandAn ? <ShieldAlert size={13} /> : <ShieldCheck size={13} />}
            {!versandAn ? 'Versand AUS' : live ? 'LIVE — echte Kunden!' : 'Test-Modus'}
          </span>
        )}
      </div>

      <div style={{ padding: '1rem' }}>
        {loading ? (
          <div style={{ color: '#666', textAlign: 'center', padding: '1rem' }}>Lade Einstellungen…</div>
        ) : !settings ? (
          <div style={{ color: '#c62828' }}>Einstellungen nicht verfügbar.</div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <label style={labelStyle}>
                <input
                  type="checkbox"
                  checked={settings.emailVersandAktiv}
                  onChange={(e) => patch({ emailVersandAktiv: e.target.checked })}
                />
                <strong>E-Mail-Versand global aktiv</strong>
                <span style={{ color: '#888' }}>(Kill-Switch — aus = es geht garantiert keine Mail raus)</span>
              </label>

              <label style={labelStyle}>
                <input
                  type="checkbox"
                  checked={settings.testModus}
                  onChange={(e) => patch({ testModus: e.target.checked })}
                />
                Test-Modus
                <span style={{ color: '#888' }}>
                  (nur Aufträge aus der Liste unten, Empfänger immer {settings.testRecipient})
                </span>
              </label>

              {versandAn && live && (
                <div style={{
                  padding: '0.5rem 0.75rem', backgroundColor: '#fdecea', border: '1px solid #f5c6cb',
                  borderRadius: '6px', fontSize: '0.8rem', color: '#c62828',
                }}>
                  ⚠️ <strong>LIVE + Versand AN:</strong> Mails gehen an <strong>echte Kunden</strong>. Nur bewusst aktivieren.
                </div>
              )}

              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>
                  Test-Auftragsnummern (kommagetrennt) — nur diese werden im Test-Modus verarbeitet:
                </label>
                <textarea
                  value={settings.testAuftragsNummern}
                  onChange={(e) => patch({ testAuftragsNummern: e.target.value })}
                  rows={2}
                  placeholder="z.B. 271230, 271231"
                  style={{
                    width: '100%', padding: '0.5rem', border: '1px solid #DAD9D9', borderRadius: '4px',
                    fontSize: '0.85rem', fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={save}
                  disabled={saving}
                  style={{
                    padding: '0.5rem 1rem', backgroundColor: '#E9FF57', color: '#1A1A1A', border: 'none',
                    borderRadius: '6px', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.875rem',
                  }}
                >
                  <Save size={15} /> {saving ? 'Speichere…' : 'Einstellungen speichern'}
                </button>

                <span style={{ color: '#DAD9D9' }}>|</span>

                <input
                  type="text"
                  value={testOrderId}
                  onChange={(e) => setTestOrderId(e.target.value)}
                  placeholder="Auftragsnr. für Test-Mail"
                  style={{
                    padding: '0.5rem', border: '1px solid #DAD9D9', borderRadius: '4px',
                    fontSize: '0.85rem', fontFamily: 'monospace', width: '190px',
                  }}
                />
                <button
                  onClick={sendTestMail}
                  style={{
                    padding: '0.5rem 1rem', backgroundColor: '#1A1A1A', color: '#fff', border: 'none',
                    borderRadius: '6px', fontWeight: 600, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.875rem',
                  }}
                  title={`Sendet eine Mustermail an ${settings.testRecipient}`}
                >
                  <Send size={15} /> Test-Mail senden
                </button>

                <button
                  onClick={runDiagnose}
                  style={{
                    padding: '0.5rem 1rem', backgroundColor: '#fff', color: '#1A1A1A', border: '1px solid #DAD9D9',
                    borderRadius: '6px', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem',
                  }}
                  title="Read-only: zeigt, in welchem Vorlagen-Topf die Erstinfo-/Update-Vorlagen liegen"
                >
                  Vorlagen-Diagnose
                </button>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.8rem', color: '#555' }}>Änderungserkennung testen (Auftrag oben):</span>
                <input
                  type="text"
                  value={cdKw}
                  onChange={(e) => setCdKw(e.target.value)}
                  placeholder="KW34"
                  style={{
                    padding: '0.5rem', border: '1px solid #DAD9D9', borderRadius: '4px',
                    fontSize: '0.85rem', fontFamily: 'monospace', width: '110px',
                  }}
                />
                <button
                  onClick={runChangeDetection}
                  style={{
                    padding: '0.5rem 1rem', backgroundColor: '#1A1A1A', color: '#fff', border: 'none',
                    borderRadius: '6px', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem',
                  }}
                  title="Führt die echte Automatik aus: erste KW = Erstinfo, gleiche = keine Mail, geänderte = Update"
                >
                  KW verarbeiten
                </button>
              </div>

              <div style={{ fontSize: '0.75rem', color: '#888' }}>
                Vorlagen: Erstinfo #{settings.templates.erstinfo}, Update #{settings.templates.update} ·
                max. {settings.maxMailsProLauf} Mails/Lauf · Versand über info@anstoss24.de
              </div>
            </div>

            {msg && (
              <div style={{
                marginTop: '0.75rem', padding: '0.5rem 0.75rem', borderRadius: '4px', fontSize: '0.85rem',
                backgroundColor: msg.ok ? '#e8f5e9' : '#fdecea',
                border: `1px solid ${msg.ok ? '#a5d6a7' : '#f5c6cb'}`,
                color: msg.ok ? '#2e7d32' : '#c62828',
              }}>
                {msg.text}
              </div>
            )}

            {diagnose && (
              <pre style={{
                marginTop: '0.75rem', padding: '0.75rem', backgroundColor: '#1A1A1A', color: '#E9FF57',
                borderRadius: '6px', fontSize: '0.72rem', lineHeight: 1.5, maxHeight: '320px',
                overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>
                {diagnose}
              </pre>
            )}
          </>
        )}
      </div>
    </div>
  );
}
